import { useQuery } from "@apollo/client/react";
import type { DeepPartial } from "@apollo/client/utilities";
import type { Dispatch, SetStateAction } from "react";
import { useContext, useEffect, useRef, useState } from "react";
import {
  LuCheck,
  LuCloudDownload as LuDownloadCloud,
  LuUsers,
} from "react-icons/lu";

import { subscribeUserToFeed } from "@/backend/focus";
import { SubscribeButton } from "@/components/app/subscribe-button";
import DesoAvatar from "@/components/shared/deso-avatar";
import { TriggerLoggedInModalWindow } from "@/components/shared/is-logged-in";
import { Spinner } from "@/components/shared/spinner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { toast } from "@/components/ui/use-toast";
import { UserContext } from "@/contexts/user-context";
import {
  AccountByPublicKeyDocument,
  type UserAssociation,
  UserAssociationsCountDocument,
} from "@/graphql/codegen/graphql";
import { useGetSubscriptionTiers } from "@/hooks-api/use-get-subscription-tiers";
import { AssociationType } from "@/utils/constants";
import { formatUSDCents } from "@/utils/currency";
import { pluralize } from "@/utils/helpers";
import { processRawPostContentToHTMLContent } from "@/utils/post-utils";
import type { TierSchema } from "@/utils/subscriptions";
import { getUserDisplayName, getUserName } from "@/utils/usernames";

export const FeedStoreItem = ({
  feedItem,
  setActiveFeedId,
  activeFeedId,
}: {
  feedItem: DeepPartial<UserAssociation>;
  activeFeedId: string | null;
  setActiveFeedId: Dispatch<SetStateAction<string | null>>;
}) => {
  const [loading, setLoading] = useState(false);
  const { authorizeGeneralDerivedKey, currentUserPublicKey } =
    useContext(UserContext);
  const isActive = activeFeedId === feedItem.extraData.FeedId;
  const { getSubscriptionTiers, loadingSubscriptionTiers } =
    useGetSubscriptionTiers();
  const { confirm } = useConfirm();
  const [tierPrice, setTierPrice] = useState<number | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const didSetIsSubscribed = useRef(false);

  const { data: subscriberCountData, loading: subscriberCountLoading } =
    useQuery(UserAssociationsCountDocument, {
      variables: {
        filter: {
          associationType: {
            equalTo: AssociationType.FOCUS_FEED_ACTIVE_SUBSCRIPTION,
          },
          associationValue: {
            equalTo: "FOCUS_FEED_ACTIVE_SUBSCRIPTION",
          },
          feedId: {
            equalTo: feedItem.extraData.FeedId,
          },
        },
      },
    });

  const {
    FeedId,
    FeedDescription,
    FeedPublicKey,
    FeedName,
    AccessGroupKeyName,
  } = feedItem.extraData;

  const { data: feedCreatorData } = useQuery(AccountByPublicKeyDocument, {
    variables: { publicKey: FeedPublicKey },
  });

  const feedCreator = feedCreatorData?.accountByPublicKey;

  useEffect(() => {
    (async () => {
      const tiers = await getSubscriptionTiers({
        variables: {
          filter: {
            accessGroupOwnerPublicKey: { equalTo: FeedPublicKey || "" },
            paymentCadenceDays: { notEqualTo: -1 },
            accessGroupKeyName: {
              equalTo: AccessGroupKeyName || "",
            },
          },
        },
      });
      const tierUSDCentsPrice =
        Number(
          tiers?.data?.subscriptionTiers?.nodes[0]?.extraData?.CostUsdCents,
        ) || 0;
      setTierPrice(tierUSDCentsPrice);
    })();
  }, [
    FeedPublicKey,
    AccessGroupKeyName,
    currentUserPublicKey,
    getSubscriptionTiers,
  ]);

  if (!feedCreator) {
    return null;
  }

  const subscribersCount = Number(
    subscriberCountData?.userAssociations?.totalCount,
  );

  const updateActiveFeed = async () => {
    const confirmed = await confirm({
      description: (
        <span>
          Are you sure you would like to set <strong>{FeedName}</strong> as your
          active feed?
        </span>
      ),
    });

    if (!confirmed) {
      return;
    }

    try {
      const { success, error } = await authorizeGeneralDerivedKey({
        desoLimit: 0.1,
      });

      if (!success) {
        throw error;
      }

      await subscribeUserToFeed({
        publicKey: currentUserPublicKey,
        feedPublicKey: FeedPublicKey,
        feedId: FeedId,
      });

      setActiveFeedId(FeedId);

      toast({
        variant: "success",
        title: "Success",
        description: `${FeedName} is now your active feed!`,
      });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `An error happened when setting active feed`,
        details: createToastErrorDetails(e),
      });
    }
  };

  const renderSubscribeButton = () => {
    return (
      <SubscribeButton
        size="xs"
        creator={feedCreator}
        renderContext="profile"
        subscriptionAccessGroupKeyName={AccessGroupKeyName}
        loadTierMembershipsOnRender
        loading={loadingSubscriptionTiers}
        renderContent={(props) => (
          <RenderSubscribeButtonContent
            {...props}
            {...{
              didSetIsSubscribed,
              setIsSubscribed,
              AccessGroupKeyName,
              tierPrice,
            }}
          />
        )}
        onSubscribeSuccess={async () => {
          await updateActiveFeed();
        }}
      />
    );
  };

  return (
    <div
      className="
        flex w-full rounded-2xl border
        hover:bg-accent
        sm:p-6
      "
    >
      <div className="flex w-full flex-col">
        <div className="size-full p-0">
          <div
            className="
              flex flex-col items-stretch justify-between
              sm:flex-row
            "
          >
            <div
              className="
                flex w-full flex-col gap-2 p-4
                sm:w-2/3 sm:p-0
              "
            >
              <p
                className="
                  flex items-center gap-2 font-sans text-base font-semibold
                  text-muted-foreground
                "
              >
                {FeedName}
                {!!tierPrice && (
                  <Badge variant="outline" className="inline">
                    Paid
                  </Badge>
                )}
              </p>
              <div className="mb-4 text-sm/5 text-muted">
                {processRawPostContentToHTMLContent(FeedDescription ?? "")}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <div>
                  <DesoAvatar account={feedCreator} size={32} />
                </div>
                <div className="flex flex-col">
                  <span
                    className="
                    font-sans text-sm font-semibold text-foreground
                  "
                  >
                    {getUserDisplayName({
                      extraData: feedCreator?.extraData,
                      username: feedCreator?.username,
                      publicKey: FeedPublicKey,
                      fallback: "Anonymous",
                    })}
                  </span>
                  <span className="text-xs text-muted">
                    {getUserName({
                      account: feedCreator,
                    })}
                  </span>
                </div>
              </div>
            </div>
            <div
              className="
                relative border-t border-border-light p-4
                sm:border-0 sm:p-0 sm:text-right
              "
            >
              <div
                className="
                  flex flex-col items-start gap-3
                  sm:items-end
                "
              >
                <div
                  className="
                    flex flex-row items-start gap-3
                    sm:flex-col sm:items-end sm:justify-end
                  "
                >
                  <TriggerLoggedInModalWindow>
                    {renderSubscribeButton()}
                  </TriggerLoggedInModalWindow>
                  {isSubscribed && (
                    <div className="flex">
                      {isActive ? (
                        <Button size="xs" variant="outline" disabled>
                          Active Feed
                        </Button>
                      ) : (
                        <Button
                          size="xs"
                          variant="outline"
                          disabled={loading}
                          onClick={async () => {
                            setLoading(true);
                            await updateActiveFeed();
                            setLoading(false);
                          }}
                        >
                          {loading ? "Activating..." : "Set as Active"}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                {subscriberCountLoading ? (
                  <Spinner />
                ) : (
                  subscribersCount > 0 && (
                    <div
                      className="
                        ml-2 flex items-center gap-x-1 text-xs font-normal
                        text-gray-600
                      "
                    >
                      <LuUsers /> {pluralize(subscribersCount, "Subscriber")}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
const RenderSubscribeButtonContent = ({
  activeTierMemberships,
  didSetIsSubscribed,
  AccessGroupKeyName,
  setIsSubscribed,
  tierPrice,
}: {
  activeTierMemberships: TierSchema[];
  didSetIsSubscribed: React.MutableRefObject<boolean>;
  AccessGroupKeyName: string;
  setIsSubscribed: Dispatch<SetStateAction<boolean>>;
  tierPrice: number | null;
}) => {
  const isInstalled = activeTierMemberships.some(
    (s) => s.accessGroupKeyName === AccessGroupKeyName,
  );

  useEffect(() => {
    if (isInstalled && !didSetIsSubscribed.current) {
      didSetIsSubscribed.current = true;
      setIsSubscribed(true);
    }
  }, [isInstalled, didSetIsSubscribed, setIsSubscribed]);

  if (isInstalled) {
    return (
      <div className="flex items-center gap-2">
        <LuCheck className="text-emerald-400" /> Installed
      </div>
    );
  }

  const price =
    tierPrice && tierPrice > 0 ? formatUSDCents(tierPrice) + "/mo" : "Free";

  return (
    <>
      <LuDownloadCloud className="mr-1" /> Install for {price}
    </>
  );
};
